import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MAX_QTY } from "@/components/QuantityInput";
import { API } from "./admin-utils";
import { OrderItemPicker } from "./OrderItemPicker";
import { OrderLinesEditor } from "./OrderLinesEditor";

const EMPTY = {
  location_slug: "",
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  delivery_address: "",
  delivery_date: "",
  notes: "",
};

const GUEST = "__guest__";

const fromOrder = (o) => ({
  location_slug: o.location_slug || GUEST,
  customer_name: o.customer_name || "",
  customer_email: o.customer_email || "",
  customer_phone: o.customer_phone || "",
  delivery_address: o.delivery_address || "",
  delivery_date: o.delivery_date || "",
  notes: o.notes || "",
});

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

export function OrderEditorModal({ open, onOpenChange, order, locations, onSaved }) {
  const isEdit = !!order;
  const [form, setForm] = useState(EMPTY);
  const [lines, setLines] = useState([]);
  const [menu, setMenu] = useState([]);
  const [saving, setSaving] = useState(false);
  const [profileMissing, setProfileMissing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(order ? fromOrder(order) : EMPTY);
    setLines(order ? (order.items || []).map((it) => ({ ...it })) : []);
    setProfileMissing(false);
  }, [open, order]);

  useEffect(() => {
    if (!open || !form.location_slug) return setMenu([]);
    axios
      .get(`${API}/admin/order-menu`, {
        params: form.location_slug === GUEST ? {} : { location: form.location_slug },
      })
      .then((r) => setMenu(r.data))
      .catch(() => toast.error("Failed to load dishes"));
  }, [open, form.location_slug]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Create mode: preset site → fill from its saved Customer Profile; guest → blank for admin to complete
  const pickLocation = (slug) => {
    if (isEdit) return setForm((f) => ({ ...f, location_slug: slug }));
    const loc = locations.find((l) => l.slug === slug) || {};
    const profile = {
      customer_name: loc.contact_name || "",
      customer_email: loc.contact_email || "",
      customer_phone: loc.contact_phone || "",
      delivery_address: loc.address || "",
    };
    setProfileMissing(slug !== GUEST && !Object.values(profile).some(Boolean));
    setForm((f) => ({ ...f, location_slug: slug, ...profile }));
  };

  const addItem = (item, opt) => {
    setLines((prev) => {
      const idx = prev.findIndex((l) => l.item_id === item.id && (l.variant || null) === opt.variant);
      if (idx >= 0) {
        return prev.map((l, i) => (i === idx ? { ...l, quantity: Math.min(MAX_QTY, l.quantity + 1) } : l));
      }
      return [
        ...prev,
        { item_id: item.id, name: item.name, price: opt.price, quantity: 1, variant: opt.variant, week_number: item.week_number || null },
      ];
    });
  };

  const save = async () => {
    const missing = [
      !form.location_slug && "location",
      !form.customer_name.trim() && "customer name",
      !form.customer_email.trim() && "customer email",
      !form.delivery_date && "delivery date",
      lines.length === 0 && "at least one item",
    ].filter(Boolean);
    if (missing.length) {
      toast.error(`Please add: ${missing.join(", ")}`);
      return;
    }
    const badPrice = lines.find((l) => l.price === "" || Number.isNaN(Number(l.price)) || Number(l.price) < 0);
    if (badPrice) {
      toast.error(`Enter a valid unit price for ${badPrice.name}`);
      return;
    }
    const payload = {
      ...form,
      location_slug: form.location_slug === GUEST ? null : form.location_slug,
      notes: form.notes.trim() || null,
      items: lines.map((l) => ({ ...l, price: Number(l.price) })),
    };
    setSaving(true);
    try {
      const res = isEdit
        ? await axios.put(`${API}/admin/orders/${order.id}`, payload)
        : await axios.post(`${API}/admin/orders`, payload);
      toast.success(isEdit ? `Order ${res.data.order_number} updated` : `Order ${res.data.order_number} created`);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save order — check the fields");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0" data-testid="order-editor-modal">
        <DialogHeader className="p-5 pb-3 border-b flex-shrink-0">
          <DialogTitle className="text-base">
            {isEdit ? `Edit order ${order.order_number || ""}` : "New order"}
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto p-5 space-y-4 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Location *">
              <Select value={form.location_slug} onValueChange={pickLocation}>
                <SelectTrigger data-testid="order-editor-location">
                  <SelectValue placeholder="Choose a location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={GUEST}>Guest · public menu</SelectItem>
                  {locations.map((l) => (
                    <SelectItem key={l.slug} value={l.slug}>
                      {l.name} · {l.mode === "weekly_order" ? "Weekly Order" : "Smart Fridge"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Delivery date *">
              <Input type="date" value={form.delivery_date} onChange={set("delivery_date")} data-testid="order-editor-date" />
            </Field>
            {profileMissing && (
              <p className="sm:col-span-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="order-editor-profile-missing">
                This location has no saved Customer Profile yet — fill the details below, or add them under Locations → Edit → Customer Profile.
              </p>
            )}
            <Field label="Customer name *">
              <Input value={form.customer_name} onChange={set("customer_name")} data-testid="order-editor-name" />
            </Field>
            <Field label="Customer email *">
              <Input type="email" value={form.customer_email} onChange={set("customer_email")} data-testid="order-editor-email" />
            </Field>
            <Field label="Phone">
              <Input value={form.customer_phone} onChange={set("customer_phone")} data-testid="order-editor-phone" />
            </Field>
            <Field label="Delivery address">
              <Input value={form.delivery_address} onChange={set("delivery_address")} data-testid="order-editor-address" />
            </Field>
          </div>

          <div className="space-y-2">
            <h4 className="font-semibold text-gray-700">Items</h4>
            <OrderLinesEditor lines={lines} onChange={setLines} />
            {form.location_slug ? (
              <OrderItemPicker items={menu} onAdd={addItem} />
            ) : (
              <p className="text-xs text-gray-500">Choose a location to add dishes at that site&apos;s prices.</p>
            )}
          </div>

          <Field label="Notes">
            <Textarea rows={3} value={form.notes} onChange={set("notes")} data-testid="order-editor-notes" />
          </Field>
        </div>

        <div className="border-t p-4 flex flex-wrap items-center gap-2 flex-shrink-0 bg-white">
          <p className="text-[11px] text-gray-500 flex items-center gap-1 mr-auto" data-testid="order-editor-email-hint">
            <Mail className="w-3.5 h-3.5" />
            {isEdit ? "Changes are saved without emailing anyone." : "Customer and business will receive the usual order email."}
          </p>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="order-editor-cancel">
            Cancel
          </Button>
          <Button className="bg-[#1aa59a] hover:bg-[#158a80]" onClick={save} disabled={saving} data-testid="order-editor-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : isEdit ? "Save changes" : "Create order"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
